import { useState, useRef, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import Dropdown from "react-bootstrap/Dropdown";
import InputGroup from "react-bootstrap/InputGroup";
import "./index.scss";

import { Popup, GraphDesignTool, ConfirmDialog, PerformerConstraints, ConfigContainer } from "components";
import { deleteNode, initialNodeCoords } from "components/GraphDesignTool";
import { AnalysisTaskConfig, AnalysisConnectionConfig, AnalysisGroupConfig } from "components/Config";
import { Text, Number, Container, ColorPicker, Title } from "components/Form";

import { message, analysisDefinitions, createMap, addToMap, removeFromMap, analysisGroups, limsSpecs } from "state_management";
import { FiPlus, FiCircle, FiSettings } from "react-icons/fi";
import { Duration } from "luxon";
import RequirementsSection from "components/Lims/RequirementsSection";
import ReviewSpecification from "./ReviewSpecification";
import SystemSuitability from "./SystemSuitability";
import ConfigSection from "./ConfigSection";
import useNavigationBlocker from "utils/hooks/useBlocker"
import useT from "utils/hooks/useT"

// Blank System Suitability spec (PRD-005 Area 3 / SPEC-017): SST not required, no criteria set,
// the default six replicate injections. Unset criteria are simply not judged.
const EMPTY_SST_SPEC = {
  requiresSst: false,
  rsdAreaMax: null,
  rsdRtMax: null,
  tailingMin: null,
  tailingMax: null,
  plateCountMin: null,
  resolutionMin: null,
  injectionCount: 6,
  // F4 (SPEC-036): instrument type (HPLC/GC) for the SST template picker + the optional default
  // shared template the run pre-selects. Both null by default → inline criteria apply.
  instrumentType: null,
  defaultSstTemplateId: null,
};

// Configuration page for setting up the team's analyses.
// `readOnly` (SPEC-058 B2): rendered inside LIMS › Change control's read-only host — the save/cancel
// bar is hidden and the host's `inert` blocks every input; nothing else about the page changes.
export default function AnalysisConfigurationPage({ id, readOnly = false }) {
  const { t } = useT("common")
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeTask, setActiveTask] = useState(null);
  const [activeConnection, setActiveConnection] = useState(null);
  const [addConfig, setAddConfig] = useState(false);
  const [analysisGroupConfigId, setAnalysisGroupConfigId] = useState(null);
  const [showPerformerConstraints, setShowPerformerConstraints] = useState(false);

  // SPEC-063 (#4149) — the PARAMETER SPECIFICATION IS NO LONGER EDITED HERE. It moved to its own
  // LIMS menu, "Analysis specifications", where specifications are created and seen: it is a QC
  // method, not planning setup, and this page is planner-only, so the reviewers and supervisors who
  // must approve a specification could never reach the place it lived. Two editors for one record
  // is exactly what #4150 just finished cleaning up, so the section is REMOVED rather than made
  // read-only — a pointer to the menu is what a planner needs here.
  //
  // What DID stay is the consumable requirements. The same issue split them OUT of the versioned
  // specification: they are ordinary analysis data now — saved straight with the definition, no
  // version, no e-signature, no change-control entry — and they belong to the analysis's setup,
  // which is this page. They travel on their OWN nested `consumableSpec` key (they used to ride
  // inside `spec`), so a save from here can no longer touch the parameter specification at all.
  const showLims = useSelector((state) => state.teamStatus.showLims);
  // F8 (SPEC-040) — high-level consumable requirements for this analysis (type, quantity,
  // standardClass, notes); drives the run-time picker filtering + the send-to-review completeness
  // gate. Seeded once from the definition; included in buildLimsSpecPayload.
  const [requiredConsumables, setRequiredConsumables] = useState([]);
  const [showRequirements, setShowRequirements] = useState(false);

  // LIMS Review Specification (PRD-003 Area 1 / SPEC-007) — which competence group(s) may
  // perform the second-person review of this analysis, and how long it takes. Folded onto the
  // definition payload like the spec (nested `reviewSpec` key); lives OUTSIDE the Task Design
  // flow (not a procedure step; must NOT count toward Minimum Duration). `reviewSpecDuration` is
  // held as a Luxon Duration (the DurationInput control's value) and persisted as seconds.
  const [showReviewSpec, setShowReviewSpec] = useState(false);
  const [reviewSpecGroups, setReviewSpecGroups] = useState([]);
  const [reviewSpecDuration, setReviewSpecDuration] = useState(null);
  // SPEC-032 (F0) — whether this analysis requires the supervisor (chemist) review step. Folded
  // onto the same nested `reviewSpec` payload; default false. Foundation only (flow lands in F3).
  const [requiresSupervisorReview, setRequiresSupervisorReview] = useState(false);

  // LIMS System Suitability spec (PRD-005 Area 3 / SPEC-017) — whether this analysis definition
  // REQUIRES an SST before results can be sent to review, plus the SST acceptance criteria.
  // Folded onto the definition payload (nested `sstSpec` key), mirroring the Review Specification.
  const [showSstSpec, setShowSstSpec] = useState(false);
  const [sstSpec, setSstSpec] = useState(EMPTY_SST_SPEC);

  // SPEC-063 (#4149): the mandatory "reason for spec change" popup is GONE from this page. It
  // existed for the versioned, e-signed parameter specification, which has moved to its own menu
  // and takes the popup with it. What is left here — review spec, SST spec, consumable
  // requirements — is none of it versioned or e-signed, and the customer's decision is explicitly
  // that a consumable requirement is saved straight. Changes are still written to the audit trail
  // server-side; they simply no longer stop the planner to demand a justification first. This also
  // finishes #4073/#4074, where editing the SST popped a box about the parameter specification.

  // #4150 — the analysis-document link is NOT edited here any more. It is attached from
  // LIMS > Analysis documents, which lists every analysis with its documents, their version
  // history and the reviewer/supervisor approval actions. Two editors for one link meant the
  // planner's copy could not show any of that, so the register is the only one kept.

  const competenceGroups = useSelector((state) => state.competenceGroups);
  const analysisDefinition = useSelector((state) => state.analysisDefinitions[id]);
  const analysisNames = Object.values(useSelector((state) => state.analysisDefinitions)).map(
    (analysis) => analysis.name
  );
  const activeAnalysisGroupId = useSelector((state) => state.analysisGroups.activeAnalysisGroupId);
  const [isDraft, setIsDraft] = useState(true);

  const [tasks, setTasks] = useState(analysisDefinition ? createMap(analysisDefinition.taskDefinitions) : {});
  const [connections, setConnections] = useState(
    analysisDefinition ? createMap(analysisDefinition.taskDefinitionConnections) : {}
  );
  const [performerConstraints, setPerformerConstraints] = useState(
    analysisDefinition
      ? analysisDefinition.performerConstraints.map((constraint) => ({
          firstDefinitionId: constraint.firstDefinitionId,
          secondDefinitionId: constraint.secondDefinitionId,
          performerConstraint: constraint.performerConstraint,
        }))
      : []
  );


  const [color, setColor] = useState(analysisDefinition ? analysisDefinition.color : null);
  const [name, setName] = useState(analysisDefinition ? analysisDefinition.name : "");
  const [maxSampleCount, setMaxSampleCount] = useState(analysisDefinition ? analysisDefinition.maxSampleCount : null);
  const [minSampleCount, setMinSampleCount] = useState(analysisDefinition ? analysisDefinition.minSampleCount : 0);
  const [shouldCheckChanges, setShouldCheckChanges] = useState(true);
  const [leavePageObject, setLeavePageObject] = useState({ msg: "", location: "", display: false });
  const analysisGroupsDropdown = useSelector((state) => state.analysisGroups ? state.analysisGroups.groups : []);
  const [mode, setMode] = useState("node");
  const [nodeCoords, setNodeCoords] = useState(() => initialNodeCoords(tasks, connections));

  const minTime = useMemo(() => {
    let perSampleDurationSum = 0;
    let setupTimeSum = 0;
    let connectionTimeSum = 0;

    Object.values(tasks).forEach(task => {
      perSampleDurationSum += task.perSampleDuration || 0;
      setupTimeSum += task.setuptime || 0;
    });

    Object.values(connections).forEach(connection => {
      connectionTimeSum += connection.timeBetween.min || 0;
    });

    return perSampleDurationSum + setupTimeSum + connectionTimeSum;
  }, [tasks, connections]);

  const formatTime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor(((seconds % 86400) % 3600) / 60);
    const formattedTime = `${days}d ${hours}h ${minutes}m`;
    return formattedTime;
  }

  //Decides whether or not the user has changed any fields on the page
  function somethingHasChanged(a) {
    var msg = ""

    if (!a) {
      msg = name ? msg.concat("- " + t("analysisName") + " ") : msg
      msg = color ? msg.concat("- " + t("color") + " ") : msg
      msg = maxSampleCount ? msg.concat("- " + t("maxSampleCount") + " ") : msg
      msg = minSampleCount ? msg.concat("- " + t("minSampleCount") + " ") : msg
      msg = performerConstraints.length ? msg.concat("- " + t("performerConstraintsCount") + " ") : msg
      msg = Object.values(tasks).length ? msg.concat("- " + t("taskCount") + " ") : msg
      msg = Object.values(connections).length ? msg.concat("- " + t("connectionCount") + " ") : msg
      return msg
    }
    //Analysis basic checks
    msg = a.color !== color ? msg.concat("- " + t("color") + " ") : msg
    msg = a.maxSampleCount !== maxSampleCount ? msg.concat("- " + t("maxSampleCount") + " ") : msg
    msg = a.minSampleCount !== minSampleCount ? msg.concat("- " + t("minSampleCount") + " ") : msg
    msg = a.name !== name ? msg.concat("- " + t("analysisName") + " ") : msg

    //Task Connection checks
    var connectionsA = a.taskDefinitionConnections
    var connectionsB = Object.values(connections)
    var Connections = false

    if (connectionsA.length === connectionsB.length) {
      for (let i = 0; i < connectionsA.length; i++) {
        Connections = connectionsA[i].id !== connectionsB[i].id ? true : Connections
        Connections = connectionsA[i].isWorkdayConnection !== connectionsB[i].isWorkdayConnection ? true : Connections
        Connections = connectionsA[i].nextId !== connectionsB[i].nextId ? true : Connections
        Connections = connectionsA[i].prevId !== connectionsB[i].prevId ? true : Connections
        Connections = connectionsA[i].samplePercentage !== connectionsB[i].samplePercentage ? true : Connections
        Connections = connectionsA[i].timeBetween.max !== connectionsB[i].timeBetween.max ? true : Connections
        Connections = connectionsA[i].timeBetween.min !== connectionsB[i].timeBetween.min ? true : Connections
      }
    } else {
      msg = msg.concat("- " + t("connectionCount"))
    }
    msg = Connections ? msg.concat("- " + t("connections")) : msg

    //Performer Constraint checks
    var constra = false
    if (a.performerConstraints.length === performerConstraints.length) {
      for (let i = 0; i < a.performerConstraints.length; i++) {
        constra =
          a.performerConstraints[i].firstDefinitionId !== performerConstraints[i].firstDefinitionId ? true : constra
        constra =
          a.performerConstraints[i].performerConstraint !== performerConstraints[i].performerConstraint ? true : constra
        constra =
          a.performerConstraints[i].secondDefinitionId !== performerConstraints[i].secondDefinitionId ? true : constra
      }
    } else {
      msg = msg.concat("- " + t("performerConstraintsCount"))
    }
    msg = constra ? msg.concat("- " + t("performerConstraintsLabel") + " ") : msg

    //Task definition checks
    let tasksA = a.taskDefinitions || [];
    let tasksB = Object.values(tasks || {});

    if (tasksA.length === tasksB.length) {
        tasksA.forEach((task, i) => {
            // Check for setup time and per sample duration at task level
            let setupTimeChanged = task.setuptime !== tasksB[i].setuptime;
            let sampleDurationChanged = task.perSampleDuration !== tasksB[i].perSampleDuration;

            // Check asset requirements within each task
            const assetReqA = task.assetRequirements || [];
            const assetReqB = tasksB[i].assetRequirements || [];
            const addedOrRemovedAsset = assetReqA.length !== assetReqB.length;

            const assetRequirementsChanged = assetReqA.length === assetReqB.length && assetReqA.some((assetReq, j) => {
                return [
                    assetReq.allowedAssetIds.length !== assetReqB[j].allowedAssetIds.length ||
                    assetReq.allowedAssetIds.some((id, k) => id !== assetReqB[j].allowedAssetIds[k]),
                    assetReq.assetId !== assetReqB[j].assetId,
                    assetReq.assetOffset !== assetReqB[j].assetOffset,
                    assetReq.assetPerSampleDuration !== assetReqB[j].assetPerSampleDuration,
                    assetReq.assetSetuptime !== assetReqB[j].assetSetuptime
                ].some(Boolean);
            });

            if (assetRequirementsChanged) {
                msg += "- " + t("assetRequirementsForAsset", { index: i + 1 }) + " ";
            }
            if (setupTimeChanged) {
                msg += "- " + t("setupTimeForPeopleSetup");
            }
            if (sampleDurationChanged) {
                msg += "- " + t("perSampleDurationForPeopleSetup");
            }
            if (addedOrRemovedAsset) {
                msg += "- " + t("assetAddedOrRemoved");
            }
        });
    } else {
        msg += "- " + t("taskCount") + " ";
    }

    return msg
  }

  function FEcheckconstraintMessage(res,isDraft, isNew) {
    if (isNew && analysisNames.includes(res.name)) {
      return t("analysisNameMustBeUnique")
    } else if(!res.name) {
      return t("pleaseFillOutAnalysisName")
    } else if(!res.color) {
      return t("pleaseChooseColor")
    } else if(res.minSampleCount !== null && res.maxSampleCount !== null && res.minSampleCount > res.maxSampleCount) {
      return t("minSampleMustBeLowerThanMax")
    } else if(!isDraft && res.taskDefinitions.length === 0) {
      return t("analysisMustContainTaskDefinition")
    }
    // when converting a draft to definition, or 'Save As' in definition
    if (!(isDraft || isNew) || (isNew && !isDraft)) {
      const taskFound = res.taskDefinitions.some(task => !task.name || task.allowedGroupIds.length === 0 || typeof(task.setuptime) !== 'number')
      if(taskFound) {
        return t("pleaseFillOutRequiredTaskParams")
      }
      return ""
   }
  }

  // SPEC-021 (B2): seed the definition's own nested LIMS keys ONCE per definition (`reviewSpec` /
  // `sstSpec` / `consumableSpec` — all already loaded with the definition; no separate fetch
  // exists). The seed is guarded by a ref so a background definitions refetch never clobbers
  // in-progress edits.
  //
  // SPEC-063 (#4149): the parameter specification is no longer seeded here, and neither are its
  // versions. The R27 (#4092) dance — fetch the spec versions FIRST and seed from a pending draft
  // rather than from the effective projection, so a change awaiting four eyes was not reported back
  // to its author as lost — moved to the new specifications menu along with the editor. Nothing
  // left on this page is versioned, so the seed is synchronous again.
  const limsSpecSeededFor = useRef(null)
  const initialLimsSpecRef = useRef(null)

  useEffect(() => {
    if (!showLims || !id || !analysisDefinition || limsSpecSeededFor.current === id) return
    limsSpecSeededFor.current = id
    seedLimsSpec()
  }, [showLims, id, analysisDefinition])

  function seedLimsSpec() {
    const reviewSpec = analysisDefinition.reviewSpec
    const sst = analysisDefinition.sstSpec
    // SPEC-063: the requirements have their own nested key now. `spec` is read as a fallback only
    // so a frontend that meets a not-yet-upgraded backend still shows what is stored.
    const consumableSpec = analysisDefinition.consumableSpec
    const requirements = consumableSpec
      ? consumableSpec.requiredConsumables
      : analysisDefinition.spec && analysisDefinition.spec.requiredConsumables
    setRequiredConsumables(limsSpecs.normalizeRequiredConsumables(requirements))
    if (reviewSpec) {
      setReviewSpecGroups(reviewSpec.allowedGroupIds || [])
      setReviewSpecDuration(reviewSpec.duration ? Duration.fromObject({ seconds: reviewSpec.duration }) : null)
      setRequiresSupervisorReview(!!reviewSpec.requiresSupervisorReview)
    }
    if (sst) {
      setSstSpec({ ...EMPTY_SST_SPEC, ...sst })
    }
    // The change-detection snapshot is taken from the SAME source the editors were seeded from.
    // Only the SST slice still reads it (R25), but keeping the whole snapshot means that comparison
    // stays a plain diff of like against like.
    initialLimsSpecRef.current = JSON.stringify(
      buildLimsSpecPayload({ reviewSpec, sst, requiredConsumables: requirements })
    )
  }

  // The nested LIMS keys for the definition payload (SPEC-021 B2). `seed` lets the seeding effect
  // build the snapshot from the persisted values; without it the current editor state is used. The
  // shapes match the backend schema exactly: reviewSpec {allowedGroupIds, duration(seconds),
  // requiresSupervisorReview}, sstSpec {requiresSst, ...criteria, injectionCount}, consumableSpec
  // {requiredConsumables}.
  //
  // SPEC-063 (#4149): there is deliberately NO `spec` key here any more. Sending one — even the
  // unchanged values read off the definition — would make every ordinary definition save a write to
  // the versioned parameter specification, and an empty parameter list would read as "the analyst
  // deleted every limit" and create a draft version to that effect.
  function buildLimsSpecPayload(seed = null) {
    if (seed) {
      const sstSeed = seed.sst ? { ...EMPTY_SST_SPEC, ...seed.sst } : EMPTY_SST_SPEC
      return {
        reviewSpec: {
          allowedGroupIds: (seed.reviewSpec && seed.reviewSpec.allowedGroupIds) || [],
          duration: (seed.reviewSpec && seed.reviewSpec.duration) || null,
          requiresSupervisorReview: !!(seed.reviewSpec && seed.reviewSpec.requiresSupervisorReview),
        },
        sstSpec: sstSeed,
        consumableSpec: {
          requiredConsumables: limsSpecs.normalizeRequiredConsumables(seed.requiredConsumables),
        },
      }
    }
    return {
      reviewSpec: {
        allowedGroupIds: reviewSpecGroups,
        duration: reviewSpecDuration ? reviewSpecDuration.as("seconds") : null,
        requiresSupervisorReview,
      },
      sstSpec: { ...EMPTY_SST_SPEC, ...sstSpec },
      // SPEC-063: unversioned, saved straight with the definition.
      consumableSpec: {
        requiredConsumables: limsSpecs.normalizeRequiredConsumables(requiredConsumables),
      },
    }
  }

  // R25 (#4075): whether the SST SLICE ALONE changed. Deliberately not a whole-payload diff, which
  // covers the parameter spec and the review spec too — keying R23 on that would mean editing an
  // unrelated parameter on a definition with a legacy inline SST forced its owner to pick a
  // template first, which is exactly the lock-out R25 exists to prevent.
  const sstSpecChanged = () =>
    initialLimsSpecRef.current !== null &&
    JSON.stringify(buildLimsSpecPayload().sstSpec) !==
      JSON.stringify(JSON.parse(initialLimsSpecRef.current).sstSpec)

  // The LIMS keys merged into a DEFINITION save payload (never a draft — the draft schema rejects
  // them). SPEC-063: no `specChangeReason` any more; nothing saved from this page is versioned or
  // e-signed, so there is no change to justify before it may take effect. The backend still audits
  // every actual change.
  const limsDefinitionPayload = () => (showLims ? buildLimsSpecPayload() : {})


  const isMountedRef = useRef(true)
  const shouldCheckChangesRef = useRef(true)

  useEffect(() => {
    shouldCheckChangesRef.current = shouldCheckChanges
  }, [shouldCheckChanges])

  useEffect(() => {
    return () => {
      isMountedRef.current = false
      shouldCheckChangesRef.current = false
    }
  }, [])

  const blocker = useNavigationBlocker(
    isMountedRef.current &&
    shouldCheckChangesRef.current &&
      !leavePageObject.display &&
      somethingHasChanged(analysisDefinition));
  if (blocker.state === "blocked" && !leavePageObject.display) {
    setLeavePageObject({
      msg: somethingHasChanged(analysisDefinition),
      location: blocker.tx?.nextLocation?.pathname || "",
      display: true,
    })
  }

  // SPEC-063 (#4149): the SPEC-016 save guard (block saving while a calculated-parameter formula is
  // invalid) went with the parameter editor to the Analysis-specifications menu. Nothing on this
  // page can produce an invalid formula any more.

  // R23 (#4075) save guard: an analysis that REQUIRES an SST needs an instrument type and a default
  // template. Without them it requires an SST that can never be run from change-controlled criteria,
  // so its results can never leave the analyst — a definition that is broken by construction.
  //
  // Deliberately wired into submit and save-as ONLY, never saveDraftHandler: a draft is meant to be
  // incomplete (the whole screen switches to `required={!isDraft}` for a draft), and blocking a
  // draft save here would stop an admin parking half-finished work, which is what drafts are for.
  //
  // R25: an existing definition whose SST slice is UNTOUCHED passes, however incomplete it is. The
  // requirement applies to new or changed SST specs, never to an unrelated edit of an old one.
  const blockedByIncompleteSst = () => {
    if (!showLims || !sstSpec.requiresSst) return false
    if (sstSpec.instrumentType && sstSpec.defaultSstTemplateId) return false
    if (id && !sstSpecChanged()) return false // grandfathered — see R25 above
    dispatch(message.warning(t("limsSstTemplateRequired")))
    return true
  }

  // The definition save, kept out of the JSX (it used to be called both directly and from the
  // spec-change-reason popup; the popup is gone with SPEC-063, the shape is kept because it reads
  // better here than inline in the submit handler).
  const saveDefinition = () => {
        setName(name.trim())
        shouldCheckChangesRef.current = false
        setShouldCheckChanges(false)
        const trimmedName = name.trim()
        const res = {
          name: trimmedName,
          color,
          maxSampleCount,
          minSampleCount,
          performerConstraints,
          analysisGroupIds: activeAnalysisGroupId ? [activeAnalysisGroupId] : [],
          // SPEC-021 (B2): the review/SST/consumable specs travel ON the definition payload
          // (definitions only). SPEC-063: the parameter specification no longer does.
          ...limsDefinitionPayload(),
          taskDefinitions: Object.values(tasks).map((task) => ({
            coordinates: nodeCoords[task.id],
            name: task.name,
            tempId: String(task.id),
            allowedGroupIds: task.allowedGroupIds ? task.allowedGroupIds.filter((groupId) => competenceGroups[groupId]) : [],
            assetRequirements: task.assetRequirements ? task.assetRequirements  : [],
            setuptime: task.setuptime,
            perSampleDuration: task.perSampleDuration,
            description: task.description,
          })),
          taskDefinitionConnections: Object.values(connections).map((connection) => ({
            tempNextId: String(connection.nextId),
            tempPrevId: String(connection.prevId),
            samplePercentage: connection.samplePercentage,
            timeBetween: connection.timeBetween,
            isWorkdayConnection: connection.isWorkdayConnection,
          })),
        }
        if(FEcheckconstraintMessage(res,false,false)) {
          return dispatch(message.warning(FEcheckconstraintMessage(res,false,false)))
        }
        if (!id) {
          dispatch(analysisDefinitions.create(res))
            .then(() => navigate("/setup/analyses"))
        } else {
          // Ugly quick fix to prevent duplication
          return dispatch(analysisDefinitions.edit(res, id))
            .then(() => navigate("/setup/analyses"))
        }
  }
  return (
    <>
    <ConfigContainer
      disabled={readOnly}
      title={ analysisDefinition ? (analysisDefinition.isDraft ? t("analysisDraft") : t("analysisDefinition") ) : t("analysisDraft")}
      cancelHandler={() => {
          shouldCheckChangesRef.current = false
          setShouldCheckChanges(false)
          navigate("/setup/analyses")
        }}
      submitHandler={() => {
        if (blockedByIncompleteSst()) return
        saveDefinition()
      }}
      saveAsHandler={() => {
        if (blockedByIncompleteSst()) return
        setIsDraft(false)
        setName(name.trim())
        const trimmedName = name.trim()
        setShouldCheckChanges(false)
        const res = {
          name: trimmedName,
          color,
          maxSampleCount,
          minSampleCount,
          performerConstraints,
          analysisGroupIds: activeAnalysisGroupId ? [activeAnalysisGroupId] : [],
          // SPEC-021 (B2): "Save As" creates a NEW definition carrying the edited review/SST specs
          // and consumable requirements. SPEC-063: the copy starts with NO parameter specification —
          // it is authored in LIMS › Analysis specifications, and silently cloning a signed, version-
          // controlled specification onto a new analysis would forge its approval.
          ...limsDefinitionPayload(),
          taskDefinitions: Object.values(tasks).map((task) => ({
            name: task.name,
            tempId: String(task.id),
            allowedGroupIds: task.allowedGroupIds ? task.allowedGroupIds.filter((groupId) => competenceGroups[groupId]) : [],
            assetRequirements: task.assetRequirements ? task.assetRequirements  : [],
            setuptime: task.setuptime,
            perSampleDuration: task.perSampleDuration,
            description: task.description,
          })),
          taskDefinitionConnections: Object.values(connections).map((connection) => ({
            tempNextId: String(connection.nextId),
            tempPrevId: String(connection.prevId),
            samplePercentage: connection.samplePercentage,
            timeBetween: connection.timeBetween,
            isWorkdayConnection: connection.isWorkdayConnection,
          })),
        }
        if(FEcheckconstraintMessage(res,isDraft,true)) {
          return dispatch(message.warning(FEcheckconstraintMessage(res,isDraft,true)))
        }
        dispatch(analysisDefinitions.create(res))
          .then(() => navigate("/setup/analyses"))
      }}
      saveDraftHandler={() => {
        setIsDraft(true)
        setName(name.trim())
        shouldCheckChangesRef.current = false
        setShouldCheckChanges(false)
        const trimmedName = name.trim()
        const res = {
          name: trimmedName,
          color,
          maxSampleCount,
          minSampleCount,
          performerConstraints,
          analysisGroupIds: activeAnalysisGroupId ? [activeAnalysisGroupId] : [],
          taskDefinitions: Object.values(tasks).map((task) => ({
            coordinates: nodeCoords[task.id],
            name: task.name,
            tempId: String(task.id),
            allowedGroupIds: task.allowedGroupIds ? task.allowedGroupIds.filter((groupId) => competenceGroups[groupId]) : [],
            assetRequirements: task.assetRequirements ? task.assetRequirements  : [],
            setuptime: task.setuptime ? task.setuptime : 0,
            perSampleDuration: task.perSampleDuration,
            description: task.description,
          })),
          taskDefinitionConnections: Object.values(connections).map((connection) => ({
            tempNextId: String(connection.nextId),
            tempPrevId: String(connection.prevId),
            samplePercentage: connection.samplePercentage,
            timeBetween: connection.timeBetween,
            isWorkdayConnection: connection.isWorkdayConnection,
          })),
        }
        if(FEcheckconstraintMessage(res,isDraft,false)) {
          return dispatch(message.warning(FEcheckconstraintMessage(res,isDraft,false)))
        }
        // NB (SPEC-021 B2): drafts do NOT carry the QC-spec keys — the analysis-draft schema
        // rejects them (422). Specs persist when the draft is saved as a definition.
        if (!id) {
          dispatch(analysisDefinitions.createDraft(res))
            .then(() => navigate("/setup/analyses"))
        } else {
          // Ugly quick fix to prevent duplication
          return dispatch(analysisDefinitions.editDraft(res, id))
        }
      }}
      saveAsName={t("saveAsNewAnalysis")}
      submitButtonName={t("save")}
      cancelButtonName={t("cancel")}
      deleteIsImportant
      isDraft={analysisDefinition ? analysisDefinition.isDraft : true}
      deleteHandler={
        id ? () => {
            shouldCheckChangesRef.current = false
            setShouldCheckChanges(false)
            dispatch(analysisDefinitions.remove(id)).then(() => navigate("/setup/analyses"))
          }
            : null
        }
      >
        {leavePageObject.display && (
          <ConfirmDialog
            onConfirm={() => {
              if (blocker.state === "blocked") {
                blocker.proceed();
              } else {
                navigate(leavePageObject.location);
              }
            }}
            onCancel={() => {
              if (blocker.state === "blocked") {
                blocker.reset();
                setLeavePageObject({ ...leavePageObject, display: false });
              } else {
                setLeavePageObject({ ...leavePageObject, display: false });
              }
            }}
          >
            <b>{t("areYouSureWantToExit")}</b>
            <div className="configurationPage-popup-list-of-changes">
              <p>{t("unsavedChangesIn")}</p>
              <ul>
                {leavePageObject.msg.split("-").slice(1).map((message, index) => (
                  <li key={index}>{message.trim()}</li>
                ))}
              </ul>
            </div>
          </ConfirmDialog>
        )}
      <div className="AnalysisConfigurationPage-metadata-container">
        <Container>
          <Title className="AnalysisName">
            {t("analysisName")}<div className="red-text">*</div>
          </Title>
          <Text required value={name} onChange={setName} />
        </Container>
        <InputGroup className="Analysis-color">
          <Title>
            {t("color")}<div className="red-text">*</div>
          </Title>
          <ColorPicker required value={color} onChange={setColor} />
        </InputGroup>
        <div className="ConfigPage-group-sample-container">
          <div className="ConfigPage-analysis-group">
          <Title>
            {t("analysisGroup")}
          </Title>
          <Dropdown
            className="AnalysisGroup-selector"
            onSelect={(eventKey) => {
              if (eventKey === "add"){
                setAddConfig(true)
              } else if (eventKey === "noGroup"){
                dispatch(analysisGroups.changeActiveAnalysisGroupId(null))
              } else {
                dispatch(analysisGroups.changeActiveAnalysisGroupId(eventKey))
              }
            }}
          >
            <Dropdown.Toggle variant="primary">{activeAnalysisGroupId ? analysisGroupsDropdown[activeAnalysisGroupId].name : t("noGroup")}</Dropdown.Toggle>
            <Dropdown.Menu>
              {Object.values(analysisGroupsDropdown).map((analysisGroup) => (
                <Dropdown.Item key={analysisGroup.id} eventKey={analysisGroup.id}>
                  {analysisGroup.name}<FiSettings className="AnalysisGroup-setting" onClick={() => {
                    setAddConfig(true)
                    setAnalysisGroupConfigId(analysisGroup.id)
                  }} />
                </Dropdown.Item>
              ))}
              <Dropdown.Divider />
              <Dropdown.Item key="add" eventKey="add">
                <FiPlus /> {t("addAnalysisGroup")}
              </Dropdown.Item>
              <Dropdown.Item key="noGroup" eventKey="noGroup">
                <FiCircle /> {t("noGroup")}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          </div>
          <div className="ConfigPage-sample-count">
            <Title>
              {t("maxSampleCount")}<div className="red-text">*</div>
            </Title>
            <Number
              style={{
                textAlign: "center",
                width: maxSampleCount !== null ? `calc(30px + ${maxSampleCount.toString().length * 9}px)` : "30px",
              }}
              required={!isDraft}
              min={1}
              value={maxSampleCount}
              onChange={setMaxSampleCount}
            />
            <Title>{t("minSampleCount")}</Title>
            <Number
              style={{
                textAlign: "center",
                width: minSampleCount !== null ? `calc(30px + ${minSampleCount.toString().length * 9}px)` : "30px",
              }}
              min={0}
              value={minSampleCount}
              onChange={setMinSampleCount}
            />
          </div>
        </div>
      </div>
      <p className="Pheading">{t("analysisDesignTool")}</p>
      <p className="Pheading ConfigurationPage">{t("taskDesignTool")}</p>
      <div className="AnalysisConfigurationPage-drawing">
        <GraphDesignTool
          required
          nodes={tasks}
          edges={connections}
          nodeColor={color ? color.toUpperCase() : "#FFFFFF"}
          onClear={() => {
            setTasks({})
            setConnections({})
          }}
          onCreateNode={(task) => {
            setTasks({ ...tasks, [task.id]: task })
          }}
          onEditNode={(task) => setActiveTask(task)}
          onDeleteNode={(id) => {
            const [newTasks, newConnections] = deleteNode(tasks, connections, id)
            setTasks(newTasks)
            setConnections(newConnections)
            setPerformerConstraints(
              performerConstraints
                .filter((constraint) => constraint.firstDefinitionId !== id)
                .filter((constraint) => constraint.secondDefinitionId !== id)
            )
          }}
          onCreateEdge={(prevId, nextId) => {
            const conn = {
              prevId,
              nextId,
              id: `${prevId}-${nextId}`,
              timeBetween: {
                min: 0,
                max: null,
              },
              samplePercentage: 100,
            }
            setConnections({ ...connections, [conn.id]: conn })
          }}
          onEditEdge={(connection) => setActiveConnection(connection)}
          configuredNodes={Object.values(tasks)
            // When editing a task you can't press "Save" before the name is filled - so this is valid!
            .filter((task) => task.name && task.allowedGroupIds?.length)
            .map((task) => task.id)}
          onModeChange={() => setMode(mode === "node" ? "edge" : "node")}
          mode={mode}
          onChangeInNodeCoords={setNodeCoords}
          isDraft={isDraft}
        />
        <div className="AnalysisConfigurationPage-time-container">
          <p>{t("minimumDuration", { time: formatTime(minTime) })}</p>
        </div>
      </div>
      {/* R1 (#4095): Task Performer Constraints leads the sections. It is the only one that is NOT
          LIMS-specific, so it also sits outside the showLims gate — which is why it is first for
          every team, LIMS or not. separator={false} because it is the first section on the page: a
          rule here would land directly under the design tool's own bottom edge. */}
      <ConfigSection
        title={t("taskPerformerConstraints")}
        open={showPerformerConstraints}
        onToggle={() => setShowPerformerConstraints(!showPerformerConstraints)}
        separator={false}
      >
        <PerformerConstraints
          tasks={tasks}
          performerConstraints={performerConstraints}
          onConstraintsChange={setPerformerConstraints}
        />
      </ConfigSection>
      {showLims && (
        <>
          <ConfigSection
            title={t("reviewSpecification")}
            open={showReviewSpec}
            onToggle={() => setShowReviewSpec(!showReviewSpec)}
          >
            <ReviewSpecification
              competenceGroups={competenceGroups}
              allowedGroupIds={reviewSpecGroups}
              onAllowedGroupIdsChange={setReviewSpecGroups}
              duration={reviewSpecDuration}
              onDurationChange={setReviewSpecDuration}
              requiresSupervisorReview={requiresSupervisorReview}
              onRequiresSupervisorReviewChange={setRequiresSupervisorReview}
            />
          </ConfigSection>
          <ConfigSection
            title={t("limsSstSection")}
            open={showSstSpec}
            onToggle={() => setShowSstSpec(!showSstSpec)}
          >
            <SystemSuitability spec={sstSpec} onChange={setSstSpec} />
          </ConfigSection>
        </>
      )}
        {showLims && (
          <>
            {/* F8 (SPEC-040): high-level consumable requirements for this analysis. SPEC-063 split
                them OUT of the versioned specification — they are saved straight with the definition
                now, with no version and no signatures — and they stay HERE, because they are part of
                the analysis's setup. They drive the run-time picker filter + the send-to-review
                completeness gate. */}
            <ConfigSection
              title={t("limsRequiredConsumables")}
              open={showRequirements}
              onToggle={() => setShowRequirements(!showRequirements)}
            >
              <RequirementsSection requirements={requiredConsumables} onChange={setRequiredConsumables} />
            </ConfigSection>
          </>
        )}
        <hr />
    </ConfigContainer>
    {activeTask && (
        <Popup onCancel={() => setActiveTask(null)}>
          <AnalysisTaskConfig
            title={t("editTask")}
            isDefinition
            task={activeTask}
            submitHandler={(res) => {
              setTasks(addToMap(tasks, { ...activeTask, ...res }))
              setActiveTask(null)
            }}
            isDraft={isDraft}
            deleteHandler={null}
            cancelHandler={() => setActiveTask(null)}
          />
        </Popup>
      )}
      {addConfig && (
        <Popup nonScrollable onCancel={() => setAddConfig(false)}>
          <AnalysisGroupConfig
            analysisGroup={analysisGroupsDropdown[analysisGroupConfigId]}
            onClose={() => {
              setAddConfig(false)
              setAnalysisGroupConfigId(null)
            }}
            onSubmit={() => {
              setAddConfig(false)
              setAnalysisGroupConfigId(null)
            }}
          />
        </Popup>
      )}
      {activeConnection && (
        <Popup nonScrollable onCancel={() => setActiveConnection(null)}>
          <AnalysisConnectionConfig
            connection={activeConnection}
            taskFrom={tasks[activeConnection.prevId]}
            taskTo={tasks[activeConnection.nextId]}
            submitHandler={(res) => {
              setConnections(addToMap(connections, { ...activeConnection, ...res }))
              setActiveConnection(null)
            }}
            deleteHandler={() => {
              setConnections(removeFromMap(connections, activeConnection))
              setActiveConnection(null)
            }}
            cancelHandler={() => setActiveConnection(null)}
          />
        </Popup>
      )}
    </>
  )
}

